import React, { useState, useEffect } from "react";
import { ProjectResponseDto } from "../common/ObjectTypes";
import { useProfile } from "../common/ProfileProvider";
import { ProjectSearchQueryType } from "../common/QueryTypes";
import { getProjects } from "../../api/projectsApi";
import ProjectsList from "./ProjectsList";
import { getCountries } from "../../api/organizationsApi";
import SideMenu from "../common/SideMenu";
import tailwindClassnames from "../common/TailwindClassnames";
import { useNavigate } from "react-router-dom";
import { OrganizationRoles } from "../common/Enums";
import SearchBarProjects from "./SearchBarProjects";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import { pageSize } from "../../api/apiUtils";

const ManageProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectResponseDto[]>([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [loading, setLoading] = useState(true);
  const { profile, profileLoading } = useProfile();
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [canAddProject, setCanAddProject] = useState(false);
  const [hasProjects, setHasProjects] = useState(false);
  const initialProjectsSearchQuery = {
    skip: 0,
    take: pageSize,
    sort_by: "project_name",
    sort_direction: "asc",
    country_id: undefined,
  };
  const [projectsSearchQuery, setProjectsSearchQuery] =
    useState<ProjectSearchQueryType>(initialProjectsSearchQuery);

  const [tempProjectsSearchQuery, setTempProjectsSearchQuery] =
    useState<ProjectSearchQueryType>(initialProjectsSearchQuery);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const onAddProject = () => {
    navigate("/addProject");
  };

  /**
   * Fetch countries when language changes.
   */
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const temp = await getCountries(i18n.language);
        setCountries(temp);
      } catch (error) {
        console.error(t("Error loading countries:"), error);
      }
    };

    loadCountries();
  }, [t]);

  /**
   * Fetch projects when search query changes for country ID.
   */
  useEffect(() => {
    handleSearch();
  }, [tempProjectsSearchQuery.country_id]);

  /**
   * Fetch initial data for profile once profile is loaded.
   */
  useEffect(() => {
    if (!profileLoading && profile) {
      const temp = profile?.project_roles.length > 0 || profile?.is_admin;
      setHasProjects(temp);
      const isAdmin = profile?.is_admin;
      const isInRoleToAddProject = profile?.org_roles.some(
        (org_role: any) =>
          org_role.role === OrganizationRoles.OWNER ||
          org_role.role === OrganizationRoles.EDITOR ||
          org_role.role === OrganizationRoles.CONTRIBUTOR,
      );
      setCanAddProject(isAdmin || isInRoleToAddProject);
    }
  }, [profileLoading, profile]);

  /**
   * Fetch projects when profile is loaded or search query changes.
   */
  useEffect(() => {
    const fetchProjects = async () => {
      setResultsLoaded(false);
      try {
        const temp = await getProjects(projectsSearchQuery);
        setProjects(temp.projects);
        setTotalProjects(temp.count);
      } catch (err) {
        console.error("Error loading projects:", err);
      } finally {
        setResultsLoaded(true);
        setLoading(false);
      }
    };
    if (!profileLoading) {
      fetchProjects();
    }
  }, [projectsSearchQuery, profileLoading]);

  const handleSearch = async () => {
    setProjectsSearchQuery(tempProjectsSearchQuery);
  };

  const handleChangeSearchQuery = (e: any) => {
    if (e) {
      let name: string;
      let value: string | boolean;
      name = e.target.name;
      value = e.target.value;
      setTempProjectsSearchQuery((prevQuery) => ({
        ...prevQuery,
        [name]: value,
      }));
    } else {
      setTempProjectsSearchQuery((prevQuery) => ({
        ...prevQuery,
        country_id: undefined,
      }));
    }
  };

  const handlePageClick = (e: any) => {
    const selectedPage = e.selected;
    const skip = Math.ceil(
      selectedPage * (projectsSearchQuery.take ?? pageSize),
    );
    setProjectsSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: skip,
    }));
  };

  return (
    <div className="flex flex-row w-full">
      <SideMenu />
      <div className="overflow-x-auto w-full mx-8 mt-8">
        <div className={tailwindClassnames.pageHeaderRow}>
          <div>
            <h1 className={tailwindClassnames.pageTitle}>{t("Projects")}</h1>
            <p className={tailwindClassnames.pageSubtitle}>
              {t("Manage projects, their members, and deployments")}
            </p>
          </div>
        </div>
        <div className={tailwindClassnames.tableCard}>
          <SearchBarProjects
            countries={countries}
            onChangeSearchQuery={handleChangeSearchQuery}
            onSearch={handleSearch}
            tempProjectsSearchQuery={tempProjectsSearchQuery}
            onAddProject={canAddProject ? onAddProject : undefined}
          />
          <ProjectsList
            projects={projects}
            totalCount={totalProjects}
            take={pageSize}
            offset={projectsSearchQuery.skip}
            loading={loading}
            resultsLoaded={resultsLoaded}
            countries={countries}
            showOrganizationsColumn={true}
            hasProjects={hasProjects}
            onPageClick={handlePageClick}
          />
        </div>
      </div>
    </div>
  );
};

export default ManageProjectsPage;
